// Tenant ("Mandant") branding without a domain of its own: the link carries
// the client id as ?m=<id>, we remember it and restyle the app from the public
// branding endpoint. Brands with their own domain (medfit, …) are static and
// win over this, see App.js.

const TENANT_ID_KEY = "tenantId";
const TENANT_BRANDING_KEY = "tenantBranding";

// Inline CSS variables set on <html>. They beat the :root rules of the brand
// stylesheets, so removing them falls back to the stylesheet values.
const derivedColors = (primary, secondary) => ({
  "--main-color": primary,
  "--main-color-light": `color-mix(in srgb, ${primary} 60%, white)`,
  "--background-active": `color-mix(in srgb, ${primary} 8%, white)`,
  "--background-light": primary,
  "--second-color": secondary || `color-mix(in srgb, ${primary} 70%, black)`,
  // Same steps as the sophyapp green in variables.css: light to dark.
  "--color-graph-1": `color-mix(in srgb, ${primary} 40%, white)`,
  "--color-graph-2": `color-mix(in srgb, ${primary} 60%, white)`,
  "--color-graph-3": `color-mix(in srgb, ${primary} 80%, white)`,
  "--color-graph-4": `color-mix(in srgb, ${primary} 90%, white)`,
  "--color-graph-5": `color-mix(in srgb, ${primary} 90%, black)`,
  "--color-graph-6": primary,
  "--color-graph-7": `color-mix(in srgb, ${primary} 60%, black)`,
});
const COLOR_VARIABLES = Object.keys(derivedColors("#000"));

const TENANT_ID_PATTERN = /^[1-9]\d*$/;

export const getTenantId = () => localStorage.getItem(TENANT_ID_KEY);

export const getCachedTenantBranding = () => {
  const tenantId = getTenantId();
  if (!tenantId) {
    return null;
  }
  try {
    const branding = JSON.parse(localStorage.getItem(TENANT_BRANDING_KEY));
    return branding && String(branding.id) === tenantId ? branding : null;
  } catch {
    return null;
  }
};

export const clearTenant = () => {
  localStorage.removeItem(TENANT_ID_KEY);
  localStorage.removeItem(TENANT_BRANDING_KEY);
};

// Store the tenant of a branding payload (public endpoint or login answer);
// null means the account has no tenant and clears it.
export const rememberTenantBranding = (branding) => {
  if (!branding) {
    clearTenant();
    return;
  }
  localStorage.setItem(TENANT_ID_KEY, String(branding.id));
  localStorage.setItem(TENANT_BRANDING_KEY, JSON.stringify(branding));
};

// Links from mails: <app>/m/<id>/rest-of-path. Their templates append paths
// to the app link, which a query would break; read like ?m=<id>.
const TENANT_PATH_PREFIX = /^\/m\/(\d+)(?=\/|$)/;

// Must run before the first render: useToken strips the whole query string
// while App renders. Removes only `m`, so auth_token/auth_data survive.
export const captureTenantFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const pathMatch = window.location.pathname.match(TENANT_PATH_PREFIX);
  if (!pathMatch && !params.has("m")) {
    return;
  }

  const pathname = pathMatch
    ? window.location.pathname.slice(pathMatch[0].length) || "/"
    : window.location.pathname;
  const tenantId = pathMatch ? pathMatch[1] : (params.get("m") || "").trim();
  if (TENANT_ID_PATTERN.test(tenantId)) {
    if (tenantId !== getTenantId()) {
      localStorage.removeItem(TENANT_BRANDING_KEY);
    }
    localStorage.setItem(TENANT_ID_KEY, tenantId);
  } else {
    // ?m=0 (or garbage) is the way back to plain sophyapp.
    clearTenant();
  }

  params.delete("m");
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    pathname + (query ? `?${query}` : "") + window.location.hash
  );
};

// Resolves to the current branding (null: no tenant), rejects on network or
// server errors so the caller can keep the cached one.
export const fetchTenantBranding = async (tenantId) => {
  const response = await fetch(
    `${process.env.REACT_APP_SOPHYAPP_API_URL}/de/v2/branding/${encodeURIComponent(tenantId)}`
  );
  if (!response.ok && response.status !== 404) {
    throw new Error(`Branding request failed with ${response.status}`);
  }
  const branding = response.ok ? await response.json() : null;

  // A login may have switched or cleared the tenant meanwhile; it wins.
  if (getTenantId() !== String(tenantId)) {
    return getCachedTenantBranding();
  }
  rememberTenantBranding(branding);
  return branding;
};

// What <head> looked like before the first tenant was applied, to restore it.
let originalHead = null;

const headElement = (selector, create) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  return element;
};

const linkElement = (rel) =>
  headElement(`link[rel='${rel}']`, () => {
    const link = document.createElement("link");
    link.rel = rel;
    return link;
  });

const metaElement = (name) =>
  headElement(`meta[name='${name}']`, () => {
    const meta = document.createElement("meta");
    meta.name = name;
    return meta;
  });

const HEAD_ELEMENTS = {
  icon: () => linkElement("icon"),
  manifest: () => linkElement("manifest"),
  appleTouchIcon: () => linkElement("apple-touch-icon"),
  appleTitle: () => metaElement("apple-mobile-web-app-title"),
  themeColor: () => metaElement("theme-color"),
};

const elementValue = (element) =>
  element.tagName === "LINK" ? element.getAttribute("href") : element.getAttribute("content");

const setElementValue = (element, value) => {
  const attribute = element.tagName === "LINK" ? "href" : "content";
  if (value) {
    element.setAttribute(attribute, value);
  } else {
    element.removeAttribute(attribute);
  }
};

// The manifest is a data: URL, so start_url and the icons must be absolute.
const manifestUrl = (branding) => {
  const iconUrls = [...new Set([branding.favicon_url, branding.logo_url].filter(Boolean))];
  const manifest = {
    name: branding.name,
    short_name: branding.name,
    start_url: `${window.location.origin}/?m=${branding.id}`,
    scope: `${window.location.origin}/`,
    display: "standalone",
    theme_color: branding.primary_color,
    background_color: "#ffffff",
    icons: iconUrls.map((src) => ({ src, sizes: "any" })),
  };
  return `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`;
};

export const applyTenantBranding = (branding) => {
  if (!originalHead) {
    originalHead = { title: document.title };
    Object.entries(HEAD_ELEMENTS).forEach(([key, element]) => {
      originalHead[key] = elementValue(element());
    });
  }

  const rootStyle = document.documentElement.style;
  Object.entries(
    derivedColors(branding.primary_color, branding.secondary_color)
  ).forEach(([name, value]) => rootStyle.setProperty(name, value));

  const icon = branding.favicon_url || branding.logo_url;
  document.title = branding.name;
  setElementValue(HEAD_ELEMENTS.icon(), icon);
  setElementValue(HEAD_ELEMENTS.manifest(), manifestUrl(branding));
  setElementValue(HEAD_ELEMENTS.appleTouchIcon(), branding.logo_url || icon);
  setElementValue(HEAD_ELEMENTS.appleTitle(), branding.name);
  setElementValue(HEAD_ELEMENTS.themeColor(), branding.primary_color);
};

export const removeTenantBranding = () => {
  const rootStyle = document.documentElement.style;
  COLOR_VARIABLES.forEach((name) => rootStyle.removeProperty(name));

  if (!originalHead) {
    return;
  }
  document.title = originalHead.title;
  Object.entries(HEAD_ELEMENTS).forEach(([key, element]) => {
    setElementValue(element(), originalHead[key]);
  });
  originalHead = null;
};
